import { jsPDF } from "jspdf";
import logoBauerMedia from "assets/BMG_Outdoor.png";
import { fontRobotoRegular, fontRobotoBold } from "./font";

/**
 *
 * @param {*} campaigns array of campaigns selected for export
 * @param {*} siteVersion fi (finland) or pl (poland)
 */
const ExportCampaigns = (campaigns, siteVersion) => {
  let translationFile = null;
  // Use FI for dev environments.
  if (siteVersion === "dev") {
    siteVersion = "fi";
  }
  try {
    translationFile = require(`constants/pdfExportVersions/${siteVersion}.json`);
  } catch (error) {
    console.error(`PDF Export version not found for domain: ${siteVersion}`, error);
  }

  const doc = new jsPDF();
  doc.addFileToVFS("Roboto-bold.ttf", fontRobotoBold);
  doc.addFileToVFS("Roboto-regular.ttf", fontRobotoRegular);
  doc.addFont("Roboto-bold.ttf", "Roboto", "bold");
  doc.addFont("Roboto-regular.ttf", "Roboto", "regular");
  const X = 20;
  const titlePos = { x: X, y: 32 };

  // Max width for anything with 10 margin on both sides
  const availableWidth = doc.internal.pageSize.getWidth() - 40;

  const drawHeader = (title) => {
    doc.setFontSize(16);
    const headerText = doc.splitTextToSize(title, availableWidth - 15).splice(0, 3);
    doc.addImage(logoBauerMedia, "png", X - 1, 10, 30, 14.7);
    doc.setFont("Roboto", "bold").text(headerText, titlePos.x, titlePos.y);
    doc.setFont("Roboto", "regular");
  };

  const isPL = siteVersion === "pl";

  campaigns.forEach((campaign, idx) => {
    let {
      title,
      type,
      requiredResolutions,
      deliveryDeadline,
      contract_number,
      starts,
      ends,
    } = campaign;

    let headerEndY = 53;
    drawHeader(title);
    // Draw required resolutions small header
    doc.setFontSize(11);

    function getRequiredResolutionText(siteVersion) {
      switch (siteVersion) {
        case "pl":
          return `${translationFile["headers"]["requiredResolution"]["pl"]} | Required resolution`;
        default:
          return "Required resolution";
      }
    }

    // Yellow resolution box dimensions
    let resolutionRowHeight = 5;
    let boxHeight = Math.ceil(requiredResolutions.length / 3) * resolutionRowHeight + 4;

    if (type === "DIGITAL") {
      doc.text(
        requiredResolutions.length > 0
          ? getRequiredResolutionText(siteVersion)
          : "No required resolutions",
        X,
        headerEndY
      );

      // Define column settings
      let columnCount = 3;

      if (requiredResolutions.length > 0) {
        if (requiredResolutions.length > 18) {
          doc.setFontSize(8);
          columnCount = 5;
          boxHeight = Math.ceil(requiredResolutions.length / 5) * resolutionRowHeight + 4;
        }
        doc.setDrawColor(255, 240, 5);
        doc.rect(X, headerEndY + 2, availableWidth, boxHeight, "S");

        // Draw the resolutions in columns
        let columnWid = availableWidth / columnCount;
        doc.setTextColor(0, 0, 0);
        let currentColumn = 0;
        let currentRow = 0;
        for (let i = 0; i < requiredResolutions.length; i++) {
          let resText;
          let mediaType = requiredResolutions[i] ? requiredResolutions[i].type : undefined;
          let resolution = requiredResolutions[i] ? requiredResolutions[i].resolution : undefined;
          mediaType =
            mediaType === "IMAGE_AND_VIDEO"
              ? "mp4/mov/jpg/png"
              : mediaType === "IMAGE"
              ? "jpg/png"
              : "";
          resolution = resolution ? resolution : "";
          resText = `${resolution}${mediaType && `(${mediaType})`}`;

          // Calculate position based on current column and row
          let resX = X + 2 + currentColumn * columnWid;
          let resY = headerEndY + 8 + currentRow * 5;

          doc.text(resText, resX, resY);

          // Move to the next column or row
          currentColumn++;
          if (currentColumn === columnCount) {
            currentColumn = 0;
            currentRow++;
          }
        }
      }
    }

    // Draw meta
    let metaPos = { x: X, y: 61 + boxHeight };
    let startX = metaPos.x;
    let startY = metaPos.y;
    let rowHeight = 5.5;
    let columnWidth = 50;

    // Define the property headers and values
    var properties = [
      {
        translationKey: "deliveryDeadline",
        header: "Delivery Deadline",
        value: deliveryDeadline || "-",
      },
      { translationKey: "deliveryCode", header: "Delivery Code", value: contract_number || "-" },
      { translationKey: "campaignType", header: "Campaign Type", value: type || "-" },
      { translationKey: "startDate", header: "Starts", value: starts || "-" },
      { translationKey: "endDate", header: "Ends", value: ends || "-" },
    ];

    // Draw the grid
    doc.setTextColor(71, 0, 163);
    doc.setFontSize(11);
    for (let i = 0; i < properties.length; i++) {
      doc.setFont("Roboto", i < 2 ? "bold" : "regular");
      if (i > 1) {
        doc.setTextColor(0, 0, 0);
      }
      let header = properties[i].header;
      let value = properties[i].value ?? "";
      let rowY = startY + i * rowHeight;

      if (isPL) {
        let tKey = properties[i].translationKey;
        let translatedHeader = translationFile["headers"][tKey]["pl"];
        rowHeight = 10;
        doc.text(translatedHeader, startX, rowY + (rowHeight - 4) / 2);
        doc.text(header, startX, rowY + (rowHeight + 4) / 2);
      } else {
        doc.text(header, startX, rowY + rowHeight / 2);
      }
      doc.text(value, startX + columnWidth, rowY + rowHeight / 2);
    }

    // Draw constant notification footers (starting Y independent of main content)
    let spacer = 8;
    let footerY = doc.internal.pageSize.getHeight() / 2 - spacer;
    const lineHeight = 4;

    doc.setFontSize(9);
    doc.setLineWidth(0.4);
    doc.setDrawColor(0, 0, 0);

    if (translationFile) {
      const drawFooterChunks = (lang, lineY) => {
        /** First chunk of text (material specification info) */
        let firstChunkStartY = lineY + spacer;
        let currentY = firstChunkStartY;
        doc.text(translationFile["footer"]["row1"][lang], X, currentY);

        currentY += lineHeight;
        doc.setTextColor(71, 0, 163);
        doc.textWithLink(translationFile["urls"]["product_specifications"][lang], X, currentY, {
          url: translationFile["urls"]["product_specifications"][lang],
          underline: true,
          color: "#4700a3",
        });
        doc.setTextColor(0, 0, 0);

        if (translationFile["footer"]["row2"]) {
          currentY += lineHeight;
          doc.text(translationFile["footer"]["row2"][lang], X, currentY);
          doc.setLineWidth(0.35);

          const before = translationFile["footer"]["row2"][lang].replace(
            translationFile["footer"]["row2underline"][lang],
            ""
          );
          const underlineStartX = X + doc.getTextWidth(before);
          const underlineEndX =
            underlineStartX + doc.getTextWidth(translationFile["footer"]["row2underline"][lang]);

          doc.line(underlineStartX, currentY + 0.7, underlineEndX, currentY + 0.7);
        }

        if (translationFile["footer"]["row3"]) {
          currentY += lineHeight;
          doc.setLineWidth(0.35);
          doc.setFont("Roboto", "bold");
          doc.text(translationFile["footer"]["row3"][lang], X, currentY);
          doc.setFont("Roboto", "regular");
        }

        const firstChunkHeight = currentY - firstChunkStartY;

        /** Second chunk (material upload info) */
        let secondChunkStartY = firstChunkStartY + firstChunkHeight + spacer;
        doc.text(translationFile["footer"]["row4"][lang], X, secondChunkStartY);
        doc.setTextColor(71, 0, 163);
        let linkX = X + doc.getTextWidth(translationFile["footer"]["row4"][lang]) + 1;
        doc.textWithLink(
          window.location.protocol + "//" + window.location.host,
          linkX,
          secondChunkStartY,
          {
            underline: true,
            color: "#4700a3",
          }
        );
        doc.setTextColor(0, 0, 0);
        doc.text(translationFile["footer"]["row5"][lang], X, secondChunkStartY + lineHeight);

        /** Third chunk (delivery code info) */
        let thirdChunkStartY = secondChunkStartY + lineHeight + spacer;
        doc.setFont("Roboto", "bold");
        doc.text(
          translationFile["footer"]["row6"][lang].replace(
            /\[DELIVERY_CODE\]/g,
            contract_number ?? ""
          ),
          X,
          thirdChunkStartY
        );

        /** Fourth chunk (campaign deadline and delivery info) */
        let fourthChunkStartY = thirdChunkStartY + spacer;
        const splitText = doc.splitTextToSize(
          translationFile["footer"]["row7"][lang],
          availableWidth
        );
        doc.setFont("Roboto", "regular");
        doc.text(splitText, X, fourthChunkStartY);

        /** Fifth chunk (contact info) */
        let fifthChunkStartY = fourthChunkStartY + spacer + (splitText.length - 1) * lineHeight;
        doc.text(translationFile["footer"]["row8"][lang], X, fifthChunkStartY);
        if (!isPL) {
          doc.text(
            translationFile["footer"]["contacts"]["campaigns"]["digital"][lang],
            X,
            fifthChunkStartY + lineHeight
          );
          doc.text(
            translationFile["footer"]["contacts"]["campaigns"]["print"][lang],
            X,
            fifthChunkStartY + 2 * lineHeight
          );
        }
      };

      translationFile["languages"].forEach((lang, index) => {
        let lineY = footerY + index * (isPL ? 62 : 76);

        if (index === 2) {
          doc.addPage();
          drawHeader(title);
          doc.setFontSize(9);
          lineY = 45;
        }

        doc.line(X, lineY, X + availableWidth, lineY);
        drawFooterChunks(lang, lineY);
      });
    }

    // Reset draw configs and start new round from new pdf page
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
    if (idx < campaigns.length - 1) {
      doc.addPage();
    }
  });

  const pdf = doc.save("Campaigns.pdf");
  if (pdf) {
    return true;
  }
};

export default ExportCampaigns;
